// CS-1732: Order product modal with practice/patient/IVR selection workflow
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { ShoppingCart } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import ConfirmationActionModal from "../../common/ConfirmationActionModal";
import { useModalStyles } from "../../common/useModalStyles";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { USER_ROLE_ENTITY_NAMES, ORDERTYPE, CAN_ACCESS_APPLICATION, PRODUCT_CATEGORY, PRODUCT_CONFIG_TYPE, PRODUCT_CONFIG_CODE, PRODUCT_CONFIG_VALUE } from "../../../constants/applicationConstants";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchPracticeLookupByUserRole, fetchPatientLookupByUserRole } from "../../../redux/slice/commonSlice";
// import { getAgreementsByPracticeAndProductCategory, PracticeProductAgreement } from "../../../redux/slice/productAgreement/createAgreementSlice";
import { fullName, formatDate, getCategoryDisplayLabel } from "../../../utils/Utility";
import { searchIVRByPatientId, IvrListDTO } from "../../../redux/slice/ivr/createIVRSlice";
import dayjs from "dayjs";
import { getPatient, getPrescriptionsByProductIdAndPatientId, usePatientReducer, Prescription } from "../../../redux/slice/patient/patientSlice";
import PrescriptionModalPopup from "../prescription/PrescriptionModalPopup";
import { submitPrescription } from "../../../redux/slice/prescription/prescriptionSlice";
import { MarketProduct } from "../../../redux/slice/exploreMarket/exploreMarketSlice";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { getProductConfigByProduct, ProductConfigRules } from "../../../service/ProductService";
import { fetchOrderDocumentById } from "../../../redux/slice/order/OrderSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import { fetchPracticeProductsDataUsingDateOfServiceAndProvider } from "../../../redux/slice/practice/practiceProductSlice";
import { tokens as themeTokens } from "../../../theme";
import InfoPopup from "../../common/InfoPopup";

interface OrderProductModalProps {
  open: boolean;
  onClose: () => void;
  product?: {
    productId: number;
    productName: string;
    manufacturerId?: number | null;
    manufacturerName?: string;
    productCategory?: string;
    extendedPrice?: number | null;
    skuAllowablePrices?: any[] | null;
    hasSKUPrice?: boolean;
  };
}

interface OrderFormData {
  practice: string | number;
  patient: string | number;
  ivr: string | number;
  ivrNotAvailable: boolean;
  product: string | number;
}

type OrderPracticeProduct = {
  practiceProductId: string | number;
  productId: string | number;
  productName: string;
  agreementStatus?: string;
  providerName?: string;
  practiceProviderId?: string | number | null;
  productCategory?: string;
};

const toNumber = (value?: string | number | null): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? undefined : numericValue;
};

const getConfigValue = (configRules: ProductConfigRules[], configType: string, configCode: string): string | null => {
  const rule = configRules.find(r => r.configType === configType && r.configCode === configCode);
  return rule?.value ?? null;
};

const OrderProductModal = ({ open, onClose, product }: OrderProductModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userInfo } = useAuthSlice();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const { patientInfoObject } = usePatientReducer();
  const colors = themeTokens(theme.palette.mode);

  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    dropdownWrapSx,
    primaryBtnSx,
  } = useModalStyles();

  // CS-1732: State management
  const [practiceList, setPracticeList] = useState<any[]>([]);
  const [allAgreements, setAllAgreements] = useState<OrderPracticeProduct[]>([]);
  const [patientList, setPatientList] = useState<any[]>([]);
  const [ivrList, setIvrList] = useState<IvrListDTO[]>([]);
  const [productConfigRules, setProductConfigRules] = useState<ProductConfigRules[]>([]);
  const [showAgreementAlert, setShowAgreementAlert] = useState(false);
  const [prescriptionList, setPrescriptionList] = useState<Prescription[]>([]);
  const [prescriptionLoading, setPrescriptionLoading] = useState(false);
  const [isPrescriptionModalOpen, setIsPrescriptionModalOpen] = useState(false);
  const [pdfContent, setPdfContent] = useState<string>("");
  const [showPDF, setShowPDF] = useState<{ isShow: boolean; title: string; fileName: string }>({ isShow: false, title: "", fileName: "" });

  const { control, watch, reset, setValue, getValues } = useForm<OrderFormData>({
    defaultValues: {
      practice: "",
      product: "",
      patient: "",
      ivr: "",
      ivrNotAvailable: false,
    },
    mode: "onChange",
  });
  const [showInfoPopup, setShowInfoPopup] = useState<{ message: string; show: boolean; title: string }>({
    message: "",
    show: false,
    title: "",
  });

  const watchedPractice = watch("practice");
  const watchedProduct = watch("product");
  const watchedPatient = watch("patient");
  const watchedIVR = watch("ivr");
  const watchedIVRNotAvailable = watch("ivrNotAvailable");

  const selectedPracticeProduct = useMemo(() => {
    if (!watchedProduct) return undefined;
    return allAgreements.find(
      (agreement) => toNumber(agreement.productId) === Number(watchedProduct)
    );
  }, [allAgreements, watchedProduct]);

  const selectedProductAgreementStatus = useMemo(() => {
    return selectedPracticeProduct?.agreementStatus?.trim() || "Not Available";
  }, [selectedPracticeProduct]);

  const isSelectedProductAgreementExecuted = useMemo(() => {
    if (product) return true;
    if (!selectedPracticeProduct) return true;
    // General category products are open access — no agreement needed
    const cat = selectedPracticeProduct.productCategory?.toLowerCase() ?? "";
    if (cat === "general") return true;
    return selectedPracticeProduct.agreementStatus?.toLowerCase() === "executed";
  }, [product, selectedPracticeProduct]);

  const ivrConfig = useMemo(() => {
    return getConfigValue(productConfigRules, PRODUCT_CONFIG_TYPE.ORDER_REQUIREMENT, PRODUCT_CONFIG_CODE.IVR);
  }, [productConfigRules]);

  const orderWorkflowType = useMemo(() => {
    return getConfigValue(productConfigRules, PRODUCT_CONFIG_TYPE.ORDER_WORKFLOW, PRODUCT_CONFIG_CODE.TYPE);
  }, [productConfigRules]);

  const showIvrSection = useMemo(() => {
    if (!product && !watchedProduct) return false;
    return ivrConfig === PRODUCT_CONFIG_VALUE.REQUIRED || ivrConfig === PRODUCT_CONFIG_VALUE.OPTIONAL;
  }, [product, watchedProduct, ivrConfig]);

  const isIvrRequired = useMemo(() => ivrConfig === PRODUCT_CONFIG_VALUE.REQUIRED, [ivrConfig]);

  const canContinue = useMemo(() => {
    if (!watchedPractice) return false;
    if (!product && !watchedProduct) return false;
    if (!isSelectedProductAgreementExecuted) return false;
    if (!watchedPatient) return false;
    if (!showIvrSection) return true; // IVR excluded — proceed
    if (isIvrRequired) return !!watchedIVR; // IVR required — must select
    return !!watchedIVR || watchedIVRNotAvailable; // IVR optional — IVR or checkbox
  }, [watchedPractice, product, watchedProduct, isSelectedProductAgreementExecuted, watchedPatient, watchedIVR, watchedIVRNotAvailable, showIvrSection, isIvrRequired]);

  const canRequestRx = useMemo(() => {
    if (!watchedPractice) return false;
    if (!product && !watchedProduct) return false;
    if (!watchedPatient) return false;
    return true;
  }, [watchedPractice, product, watchedProduct, watchedPatient]);

  useEffect(() => {
    if (!open) return;
    fetchPractices();
    if (product?.productId) {
      fetchProductConfigRulesForProduct(product.productId);
    }
  }, [open]);

  useEffect(() => {
    if (!watchedPractice) {
      setAllAgreements([]);
      setPatientList([]);
      setShowAgreementAlert(false);
      setValue("product", "");
      setValue("patient", "");
      return;
    }
    fetchAllProductsForPractice(Number(watchedPractice));
    if (product) {
      fetchPatients(Number(watchedPractice));
    }
  }, [watchedPractice]);

  useEffect(() => {
    if (product) return;
    if (watchedProduct && watchedPractice) {
      if (selectedPracticeProduct?.productId) {
        fetchProductConfigRulesForProduct(Number(selectedPracticeProduct.productId));
      }
      // CS-1775: Show agreement warning when selected product is not executed
      // Skip warning for General category products (open access, no agreement needed)
      const category = selectedPracticeProduct?.productCategory?.toLowerCase() ?? "";
      const isOpenAccessCategory = category === "general";
      setShowAgreementAlert(
        !isOpenAccessCategory &&
        selectedPracticeProduct?.agreementStatus !== undefined &&
        selectedPracticeProduct.agreementStatus?.toLowerCase() !== "executed"
      );
      // Always fetch patients when a product is selected (open access products may not have agreements)
      fetchPatients(Number(watchedPractice));
    } else if (!watchedProduct) {
      setProductConfigRules([]);
      setPatientList([]);
      setIvrList([]);
      setShowAgreementAlert(false);
      setPrescriptionList([]);
      setValue("patient", "");
    }
  }, [watchedProduct, watchedPractice, selectedPracticeProduct]);

  useEffect(() => {
    if (!watchedPatient) {
      setIvrList([]);
      setPrescriptionList([]);
      setValue("ivr", "");
      setValue("ivrNotAvailable", false);
      return;
    }
    dispatch(getPatient({ patientId: watchedPatient.toString() })).catch(() => {});
    // Fetch prescriptions when patient is selected
    fetchPrescriptionsForPatient(Number(watchedPatient),Number(watchedProduct));
  }, [watchedPatient]);

  // Fetch IVRs when patient is set and IVR section is visible
  useEffect(() => {
    if (watchedPatient && showIvrSection) {
      fetchAndFilterIVRs(Number(watchedPatient));
    } else if (!showIvrSection) {
      setIvrList([]);
      setValue("ivr", "");
      setValue("ivrNotAvailable", false);
    }
  }, [watchedPatient, showIvrSection]);

  // CS-1732: Fetch practice lookup
  const fetchPractices = async () => {
    // Conditionally pass productId only if product exists (from explore market)
    const params: any = {
      activePracticeOnly: true,
      executedOnly: true,
    };
    
    if (product?.productId) {
      params.productId = product.productId;
    }

    const response = await dispatch(fetchPracticeLookupByUserRole(params));

    if (response.meta.requestStatus === "fulfilled" && response.payload) {
      const practices = response.payload;
      setPracticeList(practices);

      // CS-1732: Auto-select for practice users
      if (
        userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE &&
        practices.length === 1
      ) {
        setValue("practice", practices[0].practiceId);
      }
    }
  };

  const fetchAllProductsForPractice = async (practiceId: number) => {
    if (product) return;
    try {
      // CS-1760: Fetch products using IVR practice product API payload
      const formattedDate = dayjs().format("MM/DD/YYYY");
      const response = await dispatch(
        fetchPracticeProductsDataUsingDateOfServiceAndProvider({
          practiceId,
          dateOfService: formattedDate,
          providerId: 0,
          moduleName: "",
          includeRxRequiredProducts: true,
        })
      );

      // CS-1760: Old agreement API logic kept commented for reference during rollout
      // const [advancedRes, conservativeRes] = await Promise.all([
      //   dispatch(getAgreementsByPracticeAndProductCategory({ practiceId, productCategory: PRODUCT_CATEGORY.ADVANCED })),
      //   dispatch(getAgreementsByPracticeAndProductCategory({ practiceId, productCategory: PRODUCT_CATEGORY.CONSERVATIVE })),
      // ]);
      // const advancedProducts = advancedRes?.payload ?? [];
      // const conservativeProducts = conservativeRes?.payload ?? [];
      // setAllAgreements([...advancedProducts, ...conservativeProducts]);

      if (response.meta.requestStatus === "fulfilled") {
        setAllAgreements(response.payload?.data ?? []);
      } else {
        setAllAgreements([]);
      }
    } catch (error) {
      console.error('Failed to fetch products for practice:', error);
      setAllAgreements([]);
    }
  };

  const fetchProductConfigRulesForProduct = async (productId: number) => {
    try {
      const response = await getProductConfigByProduct(productId);
      const configRules: ProductConfigRules[] = response?.data?.data ?? [];
      setProductConfigRules(configRules);
    } catch (error) {
      console.error('Failed to fetch product config:', error);
      setProductConfigRules([]);
    }
  };

  const fetchAndFilterIVRs = async (patientId: number) => {
    const resolvedProductId =
      product?.productId ??
      toNumber(selectedPracticeProduct?.productId);
    try {
      const response = await dispatch(searchIVRByPatientId({ patientId, requireAll: false }));
      if (response.meta.requestStatus === "fulfilled" && response.payload?.data?.length > 0) {
        const filtered = resolvedProductId
          ? response.payload.data.filter((ivr: IvrListDTO) => ivr.productId === Number(resolvedProductId))
          : response.payload.data;
        setIvrList(filtered);
      } else {
        setIvrList([]);
      }
    } catch {
      setIvrList([]);
    }
  };

  // CS-1732: Fetch patient lookup
  const fetchPatients = async (practiceId: number) => {
    const response = await dispatch(
      fetchPatientLookupByUserRole({
        activePatientOnly: true,
        practiceId: practiceId,
      })
    );

    if (response.meta.requestStatus === "fulfilled" && response.payload) {
      const patients = response.payload;
      setPatientList(patients);
    }
  };

  // Fetch prescriptions for selected patient
  const fetchPrescriptionsForPatient = async (patientId: number, productId: number) => {
    setPrescriptionLoading(true);
    try {
      const response = await dispatch(getPrescriptionsByProductIdAndPatientId({ patientId, productId }));
      if (response.meta.requestStatus === "fulfilled" && response.payload) {
        setPrescriptionList(response.payload as Prescription[]);
      } else {
        setPrescriptionList([]);
      }
    } catch {
      setPrescriptionList([]);
    } finally {
      setPrescriptionLoading(false);
    }
  };

  const handleSubmitPrescription = async (payload: any): Promise<any[] | null> => {
    if (payload) {
      const result = await dispatch(submitPrescription({ request: payload, skipLoader: false }));

      // Show warning popup if submission returned no data (same as ExploreMarket)
      if (result?.payload?.data?.length === 0) {
        setShowInfoPopup({
          message: result?.payload?.message,
          show: true,
          title: "Error Occurred While Submitting the Rx Request",
        });
      }

      if (result?.meta?.requestStatus === "fulfilled" && watchedPatient) {
        fetchPrescriptionsForPatient(Number(watchedPatient), Number(watchedProduct));
      }

      return result?.payload?.data ?? null;
    }
    return null;
  };

  // Handle prescription PDF preview
  const handlePreviewPrescription = async (rx: Prescription) => {
    if (!rx.documentId) return;
    const actionResult = await dispatch(fetchOrderDocumentById(rx.documentId));
    const docData = actionResult.payload;
    if (docData?.content) {
      setPdfContent(docData.content);
      setShowPDF({
        isShow: true,
        title: `Prescription - ${rx.patientName}`,
        fileName: rx.patientName || "Prescription",
      });
    }
  };

  // CS-1732: Practice dropdown options
  const practiceOptions = useMemo(() => {
    let practiceData = [...practiceList].sort((a, b) => a.practiceName.localeCompare(b.practiceName));
    return practiceData.map((practice) => ({
      label: practice.practiceName,
      value: practice.practiceId,
    }));
  }, [practiceList]);

  const productOptions = useMemo(() => {
    const seen = new Set<string | number>();
    return allAgreements
      .filter((agreement) => {
        if (seen.has(agreement.productId)) return false;
        seen.add(agreement.productId);
        return true;
      })
      .map((agreement) => ({
        label: agreement.productName,
        value: agreement.productId,
      }));
  }, [allAgreements]);

  // CS-1732: Patient dropdown options
  const patientOptions = useMemo(() => {
    return patientList.map((patient) => {
      const patientFullName = fullName(
        patient.title,
        patient.firstName,
        patient.middleName,
        patient.lastName
      );
      const displayLabel = patient.patientDOB 
        ? `${patientFullName} (${patient.patientDOB})` 
        : patientFullName;

      return {
        label: displayLabel,
        value: patient.patientId,
      };
    });
  }, [patientList]);

  // CS-1732: IVR dropdown options - filtered by product config
  const ivrOptions = useMemo(() => {
    return ivrList.map((ivr) => ({
      label: `${ivr.ivrNumber} - ${ivr.productName} (${formatDate(ivr.dateOfService)})`,
      value: ivr.ivrId,
    }));
  }, [ivrList]);

  // CS-1732: Navigate to create patient
  const handleAddNewPatient = () => {
    const practiceId = getValues("practice");
    if (!practiceId) return;

    // Get practice name for the selected practice
    const selectedPractice = practiceList?.find(p => p.practiceId === Number(practiceId));
    const practiceName = selectedPractice?.practiceName || null;
    navigate("/editPatient", {
      state: {
        patientDetails: {
          isPatientEdit: false,
          patientId: null,
          patientData: {
            practiceId: Number(practiceId),
            practiceName: practiceName,
            patientAddedFromEmrPopup: true,
            fromOrderProduct: true,
            productId: product?.productId ?? selectedPracticeProduct?.productId,
          },
        },
      },
    });
    handleClose();
  };

  // CS-1732: Navigate to create IVR
  const handleCreateNewIVR = async () => {
    const practiceId = getValues("practice");
    const patientId = getValues("patient");

    if (!practiceId || !patientId) return;

    const prodCategory = product?.productCategory ?? selectedPracticeProduct?.productCategory;
    const isAncillary = prodCategory?.toLowerCase().includes(PRODUCT_CATEGORY.CONSERVATIVE.toLowerCase()) || false;

    navigate("/ivr", {
      state: {
        practiceId: Number(practiceId),
        ivrEmailNotification: true,
        patientData: {
          from: CAN_ACCESS_APPLICATION.PATIENT_MANAGEMENT,
          patientId: Number(patientId),
        },
        productCategory: isAncillary ? PRODUCT_CATEGORY.CONSERVATIVE : PRODUCT_CATEGORY.ADVANCED,
      },
    });
    handleClose();
  };

  // CS-1732: Navigate to order screen (or ancillary order for collagen products)
  const handleContinueToOrder = () => {
    const formData = getValues();
    // Get the full patient object from patientList
    const selectedPatient = patientList?.find(p => p.patientId === Number(formData.patient));
    const patientData = selectedPatient ? {
      firstName: selectedPatient.firstName,
      middleName: selectedPatient.middleName,
      lastName: selectedPatient.lastName,
      dob: selectedPatient.dateOfBirth || selectedPatient.patientDOB,
      gender: selectedPatient.gender,
      patientId: selectedPatient.patientId,
    } : null;

    // Get practice details (name + lookup-sourced provider/pricing when IVR is bypassed)
    const selectedPractice = practiceList?.find(p => p.practiceId === Number(formData.practice));
    const practiceName = selectedPractice?.practiceName || null;
    const providerIdFromPractice = selectedPractice?.providerId ?? selectedPractice?.practiceProviderId ?? null;
    const providerNameFromPractice = selectedPractice?.providerName ?? selectedPractice?.practiceProviderName ?? null;
    const extendedPriceFromPractice = selectedPractice?.extendedPrice ?? selectedPractice?.practiceExtendedPrice ?? null;
    const hasPracticeSkuPriceList = Array.isArray(selectedPractice?.skuAllowablePrices)
    ? selectedPractice.skuAllowablePrices.length > 0
    : Array.isArray(selectedPractice?.skuExtendedPrices)
    ? selectedPractice.skuExtendedPrices.length > 0
    : false;
    const hasSkuFromPractice = (selectedPractice?.hasSKUPrice ?? selectedPractice?.hasSku ?? false) || hasPracticeSkuPriceList;
    const skuAllowablePricesFromPractice = selectedPractice?.skuAllowablePrices ?? selectedPractice?.skuExtendedPrices ?? null;

    // CS-1732: Get provider info from selected IVR
    const selectedIVR = !formData.ivrNotAvailable && formData.ivr 
      ? ivrList.find(ivr => ivr.ivrId === Number(formData.ivr))
      : null;
    const providerIdFromIVR = selectedIVR?.providerId || null;

    // CS-1732: Determine if this is a DIRECT_ORDER (IVR selected) or DIRECT_ORDER_WITHOUT_IVR (IVR bypassed)
    const hasIVR = !formData.ivrNotAvailable && formData.ivr;
    // CS-1732: hasSku flag — false when product has no SKU allowable rates
    // const hasSku = !!(product?.skuAllowablePrices && product.skuAllowablePrices.length > 0);

    const productFromDropdown = allAgreements?.find(p => String(p.productId) === String(formData.product));
    const providerIdFromProductAgreement = productFromDropdown?.practiceProviderId ?? null;
    const providerNameFromProductAgreement = productFromDropdown?.providerName ?? null;

    // CS-1760: Determine navigation
    const resolvedProductId = product?.productId ?? selectedIVR?.productId ?? productFromDropdown?.productId;
    const resolvedProductName = product?.productName ?? selectedIVR?.productName ?? productFromDropdown?.productName;
    const resolvedHasSku = hasSkuFromPractice;
    const resolvedSkuAllowablePrices = resolvedHasSku ? skuAllowablePricesFromPractice : null;
    const resolvedExtendedPrice = resolvedHasSku ? null : extendedPriceFromPractice;

    const resolvedProviderId = providerIdFromIVR ?? providerIdFromProductAgreement ?? providerIdFromPractice;
    const resolvedProviderName = selectedIVR?.providerName ?? providerNameFromProductAgreement ?? providerNameFromPractice;

    const isAncillaryWorkflow = orderWorkflowType === PRODUCT_CONFIG_VALUE.COLLAGEN;
    const prodCategory = product?.productCategory ?? selectedIVR?.productCategory ?? productFromDropdown?.productCategory;
    const isAncillaryByCategory = prodCategory?.toLowerCase().includes(PRODUCT_CATEGORY.CONSERVATIVE.toLowerCase()) || false;
    const isAncillary = orderWorkflowType ? isAncillaryWorkflow : isAncillaryByCategory;

    const orderDetails = selectedIVR ? {
      practiceId: Number(formData.practice),
      practiceName: practiceName,
      patientId: Number(formData.patient),
      patientFirstName: patientData?.firstName,
      patientMiddleName: patientData?.middleName,
      patientLastName: patientData?.lastName,
      ivrId: selectedIVR.ivrId,
      productId: resolvedProductId,
      productName: resolvedProductName,
      providerId: resolvedProviderId,
      providerName: resolvedProviderName,
      locationId: selectedIVR.locationId,
      extendedPrice: resolvedExtendedPrice,
      hasSku: resolvedHasSku,
      orderType: ORDERTYPE.DIRECT_ORDER,
      skuAllowablePrices: resolvedSkuAllowablePrices,
    } : {
      practiceId: Number(formData.practice),
      practiceName: practiceName,
      patientId: Number(formData.patient),
      patientFirstName: patientData?.firstName,
      patientMiddleName: patientData?.middleName,
      patientLastName: patientData?.lastName,
      productId: resolvedProductId,
      productName: resolvedProductName,
      providerId: resolvedProviderId,
      providerName: resolvedProviderName,
      extendedPrice: resolvedExtendedPrice,
      hasSku: resolvedHasSku,
      orderType: ORDERTYPE.DIRECT_ORDER,
      skuAllowablePrices: resolvedSkuAllowablePrices,
    };

    // CS-1732: Route to ancillary order for products requiring complex workflow
    // Route based on workflow rules
    if (isAncillary) {
      if (hasIVR && selectedIVR) {
        navigate("/ancillaryorder", { state: { ivrId: selectedIVR.ivrId, practiceId: Number(formData.practice), practiceName: practiceName, patientId: Number(formData.patient), providerId: selectedIVR.providerId, providerName: selectedIVR.providerName, orderType: ORDERTYPE.DIRECT_ORDER, patientData: patientInfoObject } });
      } else {
         navigate("/ancillaryorder", { state: { orderType: ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR, practiceId: Number(formData.practice), practiceName: practiceName, patientId: Number(formData.patient), patientData: patientInfoObject} }) // , providerId: providerId 
      }
    } else {
      // Route to regular order for Advanced CTP products
      if (hasIVR) {
        // IVR selected - use DIRECT_ORDER with read-only displays
        navigate("/order", {
          state: {
            orderType: ORDERTYPE.DIRECT_ORDER,
            orderDetails: orderDetails,
            orderDate: dayjs(new Date()).format("MM/DD/YYYY"),
          },
        });
      } else {
        navigate("/order", {
          state: {
            orderType: ORDERTYPE.DIRECT_ORDER_WITHOUT_IVR,
            orderDetails: orderDetails,
            practiceId: Number(formData.practice),
            patientId: Number(formData.patient),
            patientData: patientData,
            productId: resolvedProductId,
            providerId: resolvedProviderId,
            practiceProviderId: resolvedProviderId,
            // orderDate: dayjs(new Date()).format("MM/DD/YYYY"),
            practiceProductId: productFromDropdown?.practiceProductId ?? formData.product
          },
        });
      }
    }
    
    handleClose();
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      reset();
      setPracticeList([]);
      setAllAgreements([]);
      setPatientList([]);
      setIvrList([]);
      setPrescriptionList([]);
      setShowAgreementAlert(false);
      setIsPrescriptionModalOpen(false);
      setShowInfoPopup({ message: "", show: false, title: "" });
    }, 300);
  };

  const outlineBtn = {
    textTransform: "none" as const,
    fontSize: "0.8rem",
    fontWeight: 600,
    color: tokens.textSecondary,
    border: `1px solid ${tokens.border}`,
    borderRadius: "8px",
    px: 1.8,
    py: 0.6,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: tokens.accent,
      color: tokens.accent,
      backgroundColor: "rgba(20,184,166,0.05)",
    },
  };

  const labelSx = {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: tokens.textSecondary,
    mb: 0.7,
    display: "block",
  };

  const handleIVRNotAvailable = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("ivrNotAvailable", e.target.checked);
    if (e.target.checked) {
      setValue("ivr", "");
    }
  };

  const handlePracticeChange = () => {
    setShowAgreementAlert(false);
    setValue("product", "");
    setValue("patient", "");
    setValue("ivrNotAvailable", false);
    setValue("ivr", "");
    setAllAgreements([]);
    setPatientList([]);
    setIvrList([]);
    setPrescriptionList([]);
    // Only clear config rules for dashboard flow — explore market has a pre-selected product
    if (!product) {
      setProductConfigRules([]);
    }
  };

  const handleProductChange = () => {
    // CS-1775: Reset agreement warning until the newly selected product is evaluated
    setShowAgreementAlert(false);
    setValue("patient", "");
    setValue("ivr", "");
    setValue("ivrNotAvailable", false);
    setPatientList([]);
    setIvrList([]);
    setPrescriptionList([]);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <ShoppingCart size={20} color={tokens.accent} strokeWidth={1.8} />
        </Box>
        <Box>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Order Product
          </Typography>
          <Typography
            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
          >
            {product ? `${product.productName} — ${product.manufacturerName}` : "Place Order"}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 2.8,
          py: 2.2,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {/* CS-1732: Product info card - only show if product exists */}
        {product && (
          <Box
            sx={{
              backgroundColor: tokens.cardBg,
              border: `1px solid ${tokens.border}`,
              borderRadius: "12px",
              px: 2.2,
              py: 1.6,
              display: "flex",
              gap: 1.4,
              alignItems: "flex-start",
              mb: 2.2,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                backgroundColor: "rgba(20,184,166,0.12)",
                border: "1px solid rgba(20,184,166,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#14b8a6"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                fontWeight={700}
                sx={{ fontSize: "0.88rem", color: tokens.textPrimary, mb: 0.3 }}
              >
                {product.productName}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.76rem",
                  color: tokens.textSecondary,
                  lineHeight: 1.5,
                }}
              >
                Select practice, patient, and IVR to continue with order placement.
              </Typography>
              {/* CS-1732: Product category read-only label */}
              {product.productCategory && (
                <Box
                  sx={{
                    mt: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1.2,
                    py: 0.4,
                    borderRadius: "6px",
                    backgroundColor: "rgba(20,184,166,0.1)",
                    border: "1px solid rgba(20,184,166,0.2)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      color: tokens.accent,
                    }}
                  >
                    Category: {getCategoryDisplayLabel(allCodeTablesValuesMap, product.productCategory)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* CS-1732: Practice selection */}
        <Box mb={1.8}>
          <Typography sx={labelSx}>Select Practice <Typography component="span" sx={{ color: colors.success[200], fontSize: "18px" }}>*</Typography></Typography>
          <Box sx={dropdownWrapSx()}>
            <CustomDropdownField
              name="practice"
              control={control}
              options={practiceOptions}
              size="small"
              placeholderText={
                practiceOptions.length === 0
                  ? "No Practice found"
                  : "Choose a practice..."
              }
              sortOptions={false}
              disabled={
                userInfo?.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE &&
                practiceList.length === 1
              }
              onChangeSelect={() => handlePracticeChange()}
            />
          </Box>
        </Box>

        {/* CS-1732: Product selection (dashboard only) */}
        {!product && watchedPractice && (
          <Box mb={1.8}>
            <Typography sx={labelSx}>Select Product <Typography component="span" sx={{ color: colors.success[200], fontSize: "18px" }}>*</Typography></Typography>
            <Box sx={dropdownWrapSx()}>
              <CustomDropdownField
                name="product"
                control={control}
                options={productOptions}
                size="small"
                placeholderText={productOptions.length === 0 ? "No Product found" : "Choose a Product..."}
                sortOptions={false}
                onChangeSelect={() => handleProductChange()}
              />
            </Box>
          </Box>
        )}

        {/* CS-1732: Patient selection */}
        {(product || watchedProduct) && watchedPractice && (
          <Box mb={1.8}>
            <Typography sx={labelSx}>Select Patient <Typography component="span" sx={{ color: colors.success[200], fontSize: "18px" }}>*</Typography></Typography>
            <Box sx={dropdownWrapSx()}>
              <CustomDropdownField
                name="patient"
                control={control}
                options={patientOptions}
                size="small"
                placeholderText={
                  patientOptions.length === 0
                    ? "No Patient found"
                    : "Choose a Patient..."
                }
                sortOptions={false}
              />
            </Box>
            <Button
              startIcon={
                <PersonAddAlt1Icon sx={{ fontSize: "0.95rem !important" }} />
              }
              size="small"
              sx={{ ...outlineBtn, mt: 1 }}
              onClick={handleAddNewPatient}
            >
              Add New Patient
            </Button>
          </Box>
        )}

        {/* Prescription section - shown when patient is selected and product category is General */}
        {watchedPatient && (product || watchedProduct) && watchedPractice && (product?.productCategory?.toLowerCase() === "general" || selectedPracticeProduct?.productCategory?.toLowerCase() === "general") && (prescriptionLoading || prescriptionList.length > 0) && (
          <Box mb={1.8}>
            <Typography sx={labelSx}>Existing Prescriptions</Typography>
            {prescriptionLoading ? (
              <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary }}>
                Loading prescriptions...
              </Typography>
            ) : prescriptionList.length > 0 ? (
              <Box
                sx={{
                  border: `1px solid ${tokens.border}`,
                  borderRadius: "10px",
                  p: 1.5,
                  backgroundColor: tokens.cardBg,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.2,
                  maxHeight: 250,
                  overflowY: "auto",
                }}
              >
                {prescriptionList.map((rx, index) => (
                  <Box
                    key={rx.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.2,
                      borderRadius: "10px",
                      backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                      border: `1px solid ${tokens.border}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "8px",
                        backgroundColor: "rgba(20,184,166,0.12)",
                        border: "1px solid rgba(20,184,166,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <DescriptionOutlinedIcon sx={{ fontSize: "1rem", color: tokens.accent }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: tokens.textPrimary }}>
                        Prescription {index + 1}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: tokens.textSecondary, mt: 0.2 }}>
                        ({rx.integrationPrescriptionId}) · {formatDate(rx.createdAt)}
                      </Typography>
                    </Box>
                    {rx.documentId && (
                      <IconButton
                        size="small"
                        onClick={() => handlePreviewPrescription(rx)}
                        sx={{
                          color: tokens.textSecondary,
                          "&:hover": { color: tokens.accent },
                        }}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
            )
             : null
            //  (
            //   <Box>
            //     <Typography sx={{ fontSize: "0.78rem", color: tokens.textSecondary, mb: 1 }}>
            //       No prescription found for this patient.
            //     </Typography>
            //     <Button
            //       startIcon={<MedicationOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />}
            //       size="small"
            //       sx={{ ...outlineBtn }}
            //       onClick={() => setIsPrescriptionModalOpen(true)}
            //     >
            //       Create Prescription
            //     </Button>
            //   </Box>
            // )
            }
          </Box>
        )}

        {/* CS-1732: IVR selection (config-driven) */}
        {watchedPatient && showIvrSection && isSelectedProductAgreementExecuted && (
          <Box mb={1.8}>
            <Typography sx={labelSx}>
              Select Approved IVR
              {isIvrRequired && (
                <Typography component="span" sx={{ fontSize: "18px", color: colors.success[200], ml: 0.3 }}>*</Typography>
              )}
            </Typography>
            <Box sx={dropdownWrapSx()}>
              <CustomDropdownField
                name="ivr"
                control={control}
                options={ivrOptions}
                size="small"
                placeholderText={
                  ivrList.length === 0
                    ? "No IVR found"
                    : "Choose an approved IVR..."
                }
                sortOptions={false}
                disabled={watchedIVRNotAvailable || ivrOptions.length === 0}
              />
            </Box>
            <Button
              startIcon={
                <AssignmentTurnedInIcon
                  sx={{ fontSize: "0.95rem !important" }}
                />
              }
              size="small"
              sx={{ ...outlineBtn, mt: 1 }}
              onClick={handleCreateNewIVR}
            >
              Create New IVR
            </Button>

            {/* CS-1732: IVR not available checkbox (optional only) */}
            {!isIvrRequired && (
              <Box sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watchedIVRNotAvailable}
                      onChange={(e) => handleIVRNotAvailable(e)}
                      sx={{
                        color: tokens.textSecondary,
                      "&.Mui-checked": {
                        color: tokens.accent,
                      },
                      }}
                    />
                  }
                  label={
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: tokens.textPrimary,
                    }}
                  >
                      IVR verification NOT available in system
                    </Typography>
                  }
                />
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ flex: 1 }} />
        <Divider sx={{ borderColor: tokens.border, mb: 2 }} />

        {/* CS-1775: Disable continue when selected product agreement is not executed */}
        {!(product?.productCategory?.toLowerCase() === "general" || selectedPracticeProduct?.productCategory?.toLowerCase() === "general") && (
          <Button
            fullWidth
            variant="contained"
            disabled={!canContinue}
            onClick={handleContinueToOrder}
            sx={{
              ...primaryBtnSx,
              fontSize: "0.95rem",
              py: 1.35,
              backgroundColor: canContinue ? tokens.accent : undefined,
              boxShadow: canContinue
                ? "0 4px 18px rgba(20,184,166,0.35)"
                : "none",
              "&.Mui-disabled": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                color: theme.palette.mode === "dark" ? "#3d4a5c" : "#b0bec5",
              },
            }}
          >
            Continue to Order
          </Button>
        )}

        {/* Request Rx button for General category products */}
        {(product?.productCategory?.toLowerCase() === "general" || selectedPracticeProduct?.productCategory?.toLowerCase() === "general") && (
          <Button
            fullWidth
            variant="contained"
            startIcon={<MedicationOutlinedIcon sx={{ fontSize: "1.1rem !important" }} />}
            disabled={!canRequestRx}
            onClick={() => setIsPrescriptionModalOpen(true)}
            sx={{
              ...primaryBtnSx,
              mt: 0,
              fontSize: "0.95rem",
              py: 1.35,
              backgroundColor: canRequestRx ? tokens.accent : undefined,
              boxShadow: canRequestRx ? "0 4px 18px rgba(20,184,166,0.35)" : "none",
              "&.Mui-disabled": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.05)",
                color: theme.palette.mode === "dark" ? "#3d4a5c" : "#b0bec5",
              },
            }}
          >
            Request Rx
          </Button>
        )}
      </Box>

      {showAgreementAlert && (
        <ConfirmationActionModal
          open={showAgreementAlert}
          onClose={() => setShowAgreementAlert(false)}
          onConfirm={() => setShowAgreementAlert(false)}
          title="Warning"
          subtitle="Agreement required"
          message={
            <>
              An active agreement for <strong>{selectedPracticeProduct?.productName ?? "this product"}</strong> could not be found. Please contact support for assistance.
            </>
          }
          confirmText="OK"
          headerIcon={<WarningAmberIcon sx={{ color: "orange" }} />}
          showCancelButton={false}
        />
      )}

      {/* PDF Viewer for prescription preview */}
      {showPDF.isShow && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() => {
            setShowPDF({ isShow: false, title: "", fileName: "" });
            setPdfContent("");
          }}
          base64String={pdfContent}
          title={showPDF.title}
          fileName={showPDF.fileName}
        />
      )}

      {showInfoPopup.show && (
        <InfoPopup
          open={showInfoPopup.show}
          content={showInfoPopup.message}
          onClose={() => setShowInfoPopup({ message: "", show: false, title: "" })}
          severity="error"
          title={showInfoPopup.title}
        />
      )}

      {/* PrescriptionModalPopup for creating a new prescription */}
      {isPrescriptionModalOpen && (
        <PrescriptionModalPopup
          product={{
            productId: product?.productId ?? Number(selectedPracticeProduct?.productId) ?? 0,
            productName: product?.productName ?? selectedPracticeProduct?.productName ?? "",
            productCategory: product?.productCategory ?? selectedPracticeProduct?.productCategory ?? null,
            hcpcsCode: null,
            composition: null,
            allowableRate: null,
            listPrice: null,
            mues: null,
            manufacturerId: product?.manufacturerId ?? null,
            manufacturerName: product?.manufacturerName ?? null,
            isAgreementRequired: null,
            agreementStatus: selectedPracticeProduct?.agreementStatus ?? null,
            agreementMode: null,
            productLogoId: null,
            isFavorited: false,
            productStatus: null,
          } as MarketProduct}
          isOpen={isPrescriptionModalOpen}
          onClose={() => setIsPrescriptionModalOpen(false)}
          onSubmit={handleSubmitPrescription}
          initialPracticeId={watchedPractice ? Number(watchedPractice) : undefined}
          initialPatientId={watchedPatient ? Number(watchedPatient) : undefined}
        />
      )}
    </Dialog>
  );
};

export default OrderProductModal;
